/**
 * Signal fetching and decoding.
 *
 * The client asks for a *manifest* — which pyramid level, which objects, which byte ranges —
 * then fetches those bytes directly and decodes int16 itself. That is the architectural
 * commitment from DESIGN.md §10.5: today the ranges resolve to an API path, and on S3 they
 * become CloudFront URLs covered by a signed cookie, at which point the application stops
 * seeing individual fetches. Because this code already fetches by URL and range, that swap is a
 * configuration change.
 *
 * What it replaces: an endpoint that converted int16 to float and serialised JSON — about ten
 * times the bytes, per pan, built in Python. It had to cap at 500,000 samples, and a full-week
 * overview could not be expressed through it at all. A full week now costs one request of about
 * 140 KB.
 */

import { authorizedFetch } from './cognito';

const API_BASE = '/api/v1';

interface ManifestRange {
  url: string;
  offset: number;
  length: number;
  first_index: number;
  count: number;
}

interface SignalManifest {
  channel: string;
  sample_rate_hz: number;
  /** int16 digital units convert to millivolts as `value * scale + offset`. */
  scale: number;
  offset: number;
  level: number;
  factor: number;
  encoding: 'int16' | 'int16-minmax-pairs';
  total_buckets: number;
  total_bytes: number;
  total_samples: number;
  ranges: ManifestRange[];
}

/**
 * A decoded window, ready to draw.
 *
 * Either raw samples (L0, a real trace) or a min/max envelope (L1+). The distinction is kept
 * explicit rather than smoothed over, because they are different things clinically: an envelope
 * is an honest summary of a span, and it must never be mistaken for the signal itself.
 */
export interface SignalWindow {
  channel: string;
  sampleRateHz: number;
  /** Pyramid level used. 0 means raw samples. */
  level: number;
  /** Samples per bucket at this level; 1 at L0. */
  factor: number;
  startSample: number;
  spanSamples: number;
  /**
   * The recording sample the first decoded value stands for. Bucket-aligned, so at or before
   * `startSample`: the viewer places every value by its own index from here, which is what lets a
   * window wider than the view be drawn as it pans (no refetch while the view stays inside it).
   */
  firstSample: number;
  /** Present at L0 only, in millivolts. */
  samples?: Float32Array;
  /** Present above L0, in millivolts. */
  mins?: Float32Array;
  maxs?: Float32Array;
  /** Bytes actually transferred — surfaced so the latency budget stays visible in the UI. */
  bytes: number;
}

async function fetchManifest(
  studyId: string,
  channel: string,
  startSample: number,
  spanSamples: number,
  canvasPx: number,
  signal?: AbortSignal,
): Promise<SignalManifest> {
  const params = new URLSearchParams({
    channel,
    start_sample: String(Math.max(0, Math.round(startSample))),
    span_samples: String(Math.max(1, Math.round(spanSamples))),
    canvas_px: String(Math.round(canvasPx)),
  });
  // authorizedFetch (cognito.ts): a token expiring mid-scrub previously just failed outright here
  // — nothing on this path ever attempted re-auth, silent or otherwise (2026-09-14).
  //
  // `import.meta.env.DEV` (true only under `vite dev`, never in the production build): this page's
  // own origin (localhost, LOCAL_DEV.md's Mode A) is never the CDN's, even when pointed at a
  // deployed API — a signed cookie the manifest response sets lands on *this* origin, and the CDN
  // range URL it would otherwise return is a genuinely cross-origin fetch the browser correctly
  // refuses to attach that cookie to. 403, found live 2026-09-16 the first time this ran from local
  // dev instead of the deployed frontend. Tells the server to hand back API-relative URLs instead —
  // slower (through the API, not the edge), but this is local frontend development, not the
  // latency budget CloudFront exists for.
  const headers = import.meta.env.DEV ? { 'X-Force-Signal-Via-Api': '1' } : undefined;
  const res = await authorizedFetch(`${API_BASE}/studies/${studyId}/signal/manifest?${params}`, { signal, headers });
  if (!res.ok) {
    throw new Error(`Signal manifest failed (${res.status})`);
  }
  return res.json();
}

/**
 * Whether a manifest URL points back at our own API rather than at the CDN.
 *
 * The manifest returns API-relative paths (`/api/v1/signal/…`) when there is no CDN or local dev
 * asked for the fallback, and an absolute CDN URL otherwise. Only the first needs — or should
 * receive — a bearer token.
 */
const isApiUrl = (url: string) => url.startsWith(API_BASE);

async function fetchRange(range: ManifestRange, signal?: AbortSignal): Promise<Int16Array> {
  const end = range.offset + range.length - 1;
  const init: RequestInit = { headers: { Range: `bytes=${range.offset}-${end}` }, signal };
  // A CDN range fetch is authorized by the signed cookie, not by our access token, and sending
  // the token there anyway hands a bearer credential for our API to a third-party host on every
  // one of thousands of fetches — for nothing, since CloudFront ignores it (DESIGN.md §13.6 S5).
  // `credentials: 'include'` is what actually matters on that path: the cookie is cross-origin to
  // the SPA, so without it the browser sends no cookie at all and the edge returns 403.
  const res = isApiUrl(range.url)
    ? await authorizedFetch(range.url, init)
    : await fetch(range.url, { ...init, credentials: 'include' });
  // 206 is the expected response; 200 means the server ignored the Range and sent everything,
  // which is still usable but means slicing here.
  if (!res.ok) throw new Error(`Signal fetch failed (${res.status})`);
  const buffer = await res.arrayBuffer();
  const bytes =
    res.status === 200 && buffer.byteLength > range.length
      ? buffer.slice(range.offset, range.offset + range.length)
      : buffer;
  // Int16Array needs 2-byte alignment; `slice` on an ArrayBuffer gives a fresh buffer at 0.
  return new Int16Array(bytes);
}

export async function loadSignalWindow(
  studyId: string,
  channel: string,
  startSample: number,
  spanSamples: number,
  canvasPx: number,
  signal?: AbortSignal,
): Promise<SignalWindow> {
  const manifest = await fetchManifest(studyId, channel, startSample, spanSamples, canvasPx, signal);

  // Ranges are fetched in parallel. There are at most a handful — the pyramid exists precisely
  // so a view is one or two objects, not hundreds.
  const parts = await Promise.all(manifest.ranges.map((r) => fetchRange(r, signal)));

  const { scale, offset } = manifest;
  const total = parts.reduce((n, p) => n + p.length, 0);
  const firstSample = manifest.ranges.length ? manifest.ranges[0].first_index * manifest.factor : startSample;

  if (manifest.encoding === 'int16') {
    const samples = new Float32Array(total);
    let at = 0;
    for (const part of parts) {
      for (let i = 0; i < part.length; i += 1) samples[at + i] = part[i] * scale + offset;
      at += part.length;
    }
    return {
      channel: manifest.channel,
      sampleRateHz: manifest.sample_rate_hz,
      level: manifest.level,
      factor: manifest.factor,
      startSample,
      spanSamples,
      firstSample,
      samples,
      bytes: manifest.total_bytes,
    };
  }

  // Interleaved (min, max) pairs.
  const buckets = total / 2;
  const mins = new Float32Array(buckets);
  const maxs = new Float32Array(buckets);
  let bucket = 0;
  for (const part of parts) {
    for (let i = 0; i + 1 < part.length; i += 2) {
      mins[bucket] = part[i] * scale + offset;
      maxs[bucket] = part[i + 1] * scale + offset;
      bucket += 1;
    }
  }
  return {
    channel: manifest.channel,
    sampleRateHz: manifest.sample_rate_hz,
    level: manifest.level,
    factor: manifest.factor,
    startSample,
    spanSamples,
    firstSample,
    mins,
    maxs,
    bytes: manifest.total_bytes,
  };
}
